import { Fragment, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { Badge, Button, Card, Col, Collapse, Form, Row, Spinner } from 'react-bootstrap';
import { Check2, ChevronDown, ChevronRight } from 'react-bootstrap-icons';
import { ConfigField } from '@/components/ConfigField';
import { FeatureChecklist } from '@/components/FeatureChecklist';
import { ContractFields } from '@/components/config/ContractFields';
import {
  ALL_APPS,
  CHILD_GROUPS,
  CONFIG_SECTIONS,
  FIELDS,
  INTERNAL_FIELD_KEYS,
  PROD_APPS,
  SYNERGY_APP_ID,
  createModeInitialValues,
  missingRequiredOnCreate,
  effectiveCurrentValue,
  hydrateFieldValue,
  hydrateFormValues,
  presentedDefault,
  isOnSwitch,
  serializeFieldValue,
  type FieldDef,
  type FormValues,
  type SectionId,
} from '@/config/clientConfigFields';
import { billingFor, contractProblem, type Billing, type ContractFormState } from '@/config/contract';
import {
  platformSettingsService,
  PLATFORM_QUOTA_INITIAL_VALUES,
  PlatformSettings as PlatformSettingsType,
} from '@/services/platformSettingsService';
import { type ClientConfig } from '@/types';
import {
  DEFAULT_ADMIN_FEATURES,
  DEFAULT_STANDARD_FEATURES,
  featuresListToString,
  sameMembers,
  stringToFeatures,
} from '@/constants/features';
import './ClientConfigForm.css';

/** `none` and `custom` both save the apps map; `none` is the map with no app a person ticked, and hides the list. */
export type AppDeploymentMode = 'none' | 'custom' | 'prod' | 'all';

export interface ClientConfigFormProps {
  mode: 'update' | 'create';
  /** Update mode: the selected client's stored config record. */
  currentConfig?: ClientConfig;
  working: boolean;
  submitLabel: string;
  submitDisabled?: boolean;
  submitHint?: string;
  /** Receives the built payload (update: per-key diff, create: minimal config). */
  onSubmit: (payload: Partial<ClientConfig>) => void;
  onError: (message: string) => void;
  /** Notifies the page when the Region field changes (e.g. for the Quota Check tab). */
  onRegionChange?: (region: string) => void;
  /**
   * Notifies the page when the form gains/loses unsaved user edits — used to
   * gate actions that would re-hydrate the form (e.g. hygiene-panel removal).
   */
  onDirtyChange?: (dirty: boolean) => void;
  /**
   * Increment after each successful save. The bar then shows a green "Saved"
   * note until the person edits another setting.
   */
  saveCounter?: number;
  /**
   * The client's contract, partner and trial dates. They live in the portal's
   * own table, so the page holds them and saves them. The form draws them in
   * Account and saves the billing they mean as the config's `contractType`.
   */
  contract: ContractFormState;
  onContractChange: (next: ContractFormState) => void;
  /** The partners named on other clients, for the partner picker. */
  partnerNames: string[];
  /** Why a new client's contract starts where it does, e.g. "the account is in NextGen". */
  contractSuggestion?: string;
  /** Changes the save also writes outside the config (contract, partner, trial dates). Counted as unsaved. */
  extraPendingCount?: number;
  /**
   * Where the page wants the rail (search, sections, save), e.g. its left
   * column under the client card. Given, the form draws only its sections and
   * puts the rail there; null draws no rail (another view is open). Left out,
   * the form draws the rail beside itself.
   */
  railHost?: HTMLElement | null;
}

/** Human note for what removal of a key falls back to. */
const removeFallbackNote = (def: FieldDef): string => {
  if (def.inheritKey) return 'The client then uses the platform setting.';
  const d = String(def.default);
  return `The client then uses the default: ${d === '' ? '(not set)' : d}.`;
};

const searchHaystack = (def: FieldDef): string => `${def.key} ${def.label} ${def.help ?? ''}`.toLowerCase();

/**
 * Empty/missing group lists display as the recommended defaults — the diff
 * must use the SAME substitution as hydration, or a record holding an empty
 * list would silently rewrite to defaults on every unrelated save.
 */
const effectiveGroupList = (list: string[] | undefined, defaults: string[]): string[] =>
  list && list.length > 0 ? list : defaults;

// Search keywords for the bespoke (non-registry) controls
const APPS_CONTROL_HAYSTACK = `apps app deployment all production custom selection ${ALL_APPS.join(' ')}`.toLowerCase();
const GROUPS_CONTROL_HAYSTACK = 'groups features roles standard admin user feature checklist permissions'.toLowerCase();
const CONTRACT_CONTROL_HAYSTACK =
  'contract type billing billed credits legacy own account trial partner internal'.toLowerCase();

/** Sub-group heading inside a section: a title and one line on what the group is. */
function SubHeader({ title, description }: { title: string; description: string }) {
  return (
    <div className="config-form__subhead">
      <div className="config-form__subhead-title">{title}</div>
      <div className="config-form__subhead-desc">{description}</div>
    </div>
  );
}

const childrenOf = (key: string): FieldDef[] => FIELDS.filter((f) => f.childOf === key);

export function ClientConfigForm({
  mode,
  currentConfig,
  working,
  submitLabel,
  submitDisabled = false,
  submitHint,
  onSubmit,
  onError,
  onRegionChange,
  onDirtyChange,
  saveCounter = 0,
  contract,
  onContractChange,
  partnerNames,
  contractSuggestion,
  extraPendingCount = 0,
  railHost,
}: ClientConfigFormProps) {
  // ---- registry-driven field state -----------------------------------------
  const [values, setValues] = useState<FormValues>(() =>
    mode === 'update' ? hydrateFormValues(currentConfig) : createModeInitialValues()
  );
  // Keys marked for removal from the record (Update mode only) — saved as
  // REMOVE expressions via updateClientConfig's undefined-value convention.
  const [removedKeys, setRemovedKeys] = useState<Set<string>>(new Set());

  // ---- bespoke controls -----------------------------------------------------
  // A new client starts with no apps.
  const [appMode, setAppMode] = useState<AppDeploymentMode>('none');
  const [selectedApps, setSelectedApps] = useState<string[]>([]);
  const [groupAdmin, setGroupAdmin] = useState(featuresListToString(DEFAULT_ADMIN_FEATURES));
  const [groupStandard, setGroupStandard] = useState(featuresListToString(DEFAULT_STANDARD_FEATURES));

  // ---- UI state -------------------------------------------------------------
  // True once the user has edited anything since the last hydration. Reported
  // upward so the page can block form-resetting actions while edits are unsaved.
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty]);
  // Green "Saved" note shown after a successful save, until the next edit.
  const [justSaved, setJustSaved] = useState(false);
  const markEdited = () => {
    setDirty(true);
    setJustSaved(false);
  };
  const [search, setSearch] = useState('');
  // Feature settings a person opened or closed by hand, by the feature's key. Unset: open when one is changed.
  const [openSettings, setOpenSettings] = useState<Record<string, boolean>>({});
  const [openSections, setOpenSections] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(CONFIG_SECTIONS.map((s) => [s.id, !s.collapsed]))
  );

  // Live platform-settings (Level 1) so the quota inheritance placeholders
  // show what clients actually inherit, not a hardcoded guess.
  const [platformSettings, setPlatformSettings] = useState<PlatformSettingsType | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await platformSettingsService.get();
        if (!cancelled) setPlatformSettings(s);
      } catch {
        if (!cancelled) setPlatformSettings({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const inherited = <K extends keyof typeof PLATFORM_QUOTA_INITIAL_VALUES>(
    key: K
  ): (typeof PLATFORM_QUOTA_INITIAL_VALUES)[K] => {
    const fromRecord = platformSettings?.[key as keyof PlatformSettingsType];
    if (typeof fromRecord === typeof PLATFORM_QUOTA_INITIAL_VALUES[key] && fromRecord !== undefined) {
      return fromRecord as (typeof PLATFORM_QUOTA_INITIAL_VALUES)[K];
    }
    return PLATFORM_QUOTA_INITIAL_VALUES[key];
  };

  const inheritPlaceholder = (key: keyof typeof PLATFORM_QUOTA_INITIAL_VALUES, suffix = ''): string => {
    if (platformSettings === null) return 'Empty: the platform setting';
    return `Empty: the platform setting (${inherited(key).toLocaleString()}${suffix})`;
  };

  // ---- hydration (Update mode re-hydrates whenever the selected client changes)
  useEffect(() => {
    if (mode !== 'update') return;
    const cfg = currentConfig;
    setValues(hydrateFormValues(cfg));
    setRemovedKeys(new Set());
    const allApps = Boolean((cfg as Record<string, unknown> | undefined)?.allApps);
    const allProd = Boolean(cfg?.allProdApps);
    const ticked = Object.keys(cfg?.apps ?? {}).filter((a) => a !== SYNERGY_APP_ID);
    setAppMode(allApps ? 'all' : allProd ? 'prod' : ticked.length > 0 ? 'custom' : 'none');
    // Hydrate the apps map regardless of mode so dev-app additions under
    // "Production apps" survive an unrelated save (the old page dropped them).
    // Reconcile Synergy Explorer with the `synergy` toggle on the way in, so the
    // checkbox and what a save would write can never disagree — a synergy client
    // whose apps map predates this app picks it up on their next save.
    const hydratedApps = cfg?.apps ? Object.keys(cfg.apps) : [];
    setSelectedApps(
      cfg?.synergy === true
        ? Array.from(new Set([...hydratedApps, SYNERGY_APP_ID]))
        : hydratedApps.filter((a) => a !== SYNERGY_APP_ID)
    );
    const groups = (cfg as Record<string, unknown> | undefined)?.groups as
      | { admin?: string[]; standard?: string[] }
      | undefined;
    const admin = effectiveGroupList(groups?.admin, DEFAULT_ADMIN_FEATURES);
    const standard = effectiveGroupList(groups?.standard, DEFAULT_STANDARD_FEATURES);
    setGroupAdmin(featuresListToString(admin));
    setGroupStandard(featuresListToString(standard));
    setOpenSettings({});
    setDirty(false);
    // Cleared on client switch; the saveCounter effect below re-sets it right
    // after a save (it runs later in the same commit).
    setJustSaved(false);
    // A collapsed section opens when this client differs from a default in it.
    const next: Record<string, boolean> = {};
    for (const s of CONFIG_SECTIONS) {
      next[s.id] =
        !s.collapsed ||
        FIELDS.some(
          (f) =>
            f.section === s.id && !f.lockedOnUpdate && hydrateFieldValue(f, cfg) !== hydrateFieldValue(f, undefined)
        );
    }
    if (!sameMembers(admin, DEFAULT_ADMIN_FEATURES) || !sameMembers(standard, DEFAULT_STANDARD_FEATURES)) {
      next.advanced = true;
    }
    setOpenSections(next);
  }, [mode, currentConfig]);

  // Runs after the hydration effect in the same commit, so a save (which both
  // re-hydrates and bumps saveCounter) lands on "Saved" rather than reset.
  // Compared against the mount-time value via a ref — a remount with a stale
  // non-zero counter (clear selection, then pick another client) must NOT
  // show "Saved" for a client that was never saved.
  const lastSaveCounter = useRef(saveCounter);
  useEffect(() => {
    if (saveCounter > lastSaveCounter.current) setJustSaved(true);
    lastSaveCounter.current = saveCounter;
  }, [saveCounter]);

  const setValue = (key: string, v: FormValues[string]) => {
    if (key === 'region' && typeof v === 'string') onRegionChange?.(v);
    markEdited();
    // Synergy Explorer follows the `synergy` toggle. It is not a production app,
    // so it reaches a client only through the apps map — and infra strips it
    // when synergy is off. Selecting/deselecting it here keeps the stored map
    // honest without the operator having to know that rule.
    if (key === 'synergy') {
      setSelectedApps((prev) =>
        v === true ? Array.from(new Set([...prev, SYNERGY_APP_ID])) : prev.filter((a) => a !== SYNERGY_APP_ID)
      );
    }
    setValues((prev) => {
      const next = { ...prev, [key]: v };
      // Synergy is a sub-capability of Native Integrations — the infra computes
      // synergyEnabled = synergy && dataConnectorsEnabled, so Synergy on its own
      // is inert. Turning it ON auto-enables its parent so the operator can't
      // save a flag that silently does nothing.
      if (key === 'synergy' && v === true) {
        next.dataConnectorsEnabled = true;
      }
      // Keep dynamic-option selects valid: if this change leaves a field's current
      // choice unavailable (dropped OR greyed-out/disabled), snap it to the first
      // ENABLED option (e.g. the Files backend once Provision Q is off, or a
      // non-dev instance greying out Expert).
      for (const def of FIELDS) {
        if (!def.dynamicOptions) continue;
        const enabled = def.dynamicOptions(next).filter((o) => !o.disabled);
        if (enabled.length > 0 && !enabled.some((o) => o.value === next[def.key])) {
          next[def.key] = enabled[0].value;
        }
      }
      // Auto-off switches that become disabled: a `disabledForcesOff` toggle must
      // not sit visually "On" while greyed out. Flip it off so stored + displayed
      // state agree.
      for (const def of FIELDS) {
        if (def.type !== 'switch' || !def.disabledForcesOff) continue;
        if (def.disabledWhen?.(next) && next[def.key] === true) {
          next[def.key] = false;
        }
      }
      return next;
    });
    setRemovedKeys((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  /**
   * The default a field is measured against. On Create it is what a new client
   * starts with (`createDefault`). On Update it is what the deploy uses when
   * the key is absent, so an existing client is never told it is "not
   * default" for lacking a value only new clients get.
   */
  const shownDefault = (def: FieldDef) =>
    mode === 'update' ? hydrateFieldValue(def, undefined) : presentedDefault(def);

  const changeContract = (next: ContractFormState) => {
    markEdited();
    onContractChange(next);
  };

  const selectAppMode = (m: AppDeploymentMode) => {
    markEdited();
    setAppMode(m);
    // No apps keeps only Synergy Explorer, which follows the Synergy switch, not a tick.
    if (m === 'none') setSelectedApps((prev) => prev.filter((a) => a === SYNERGY_APP_ID));
  };
  const editGroupAdmin = (v: string) => {
    markEdited();
    setGroupAdmin(v);
  };
  const editGroupStandard = (v: string) => {
    markEdited();
    setGroupStandard(v);
  };

  // ---- modified/non-default helpers -----------------------------------------
  const groupsNonDefault =
    !sameMembers(stringToFeatures(groupAdmin), DEFAULT_ADMIN_FEATURES) ||
    !sameMembers(stringToFeatures(groupStandard), DEFAULT_STANDARD_FEATURES);

  /** Switched on, for the Features count: a switch that is on and shown. */
  const switchIsOn = (def: FieldDef): boolean =>
    def.type === 'switch' &&
    !removedKeys.has(def.key) &&
    (!def.visibleWhen || def.visibleWhen(values)) &&
    values[def.key] === true;

  /** Changed from its default, for the other sections' counts. A value locked on Update is not a choice here. */
  const fieldChanged = (def: FieldDef): boolean =>
    !def.bespoke &&
    !(mode === 'update' && def.lockedOnUpdate) &&
    !removedKeys.has(def.key) &&
    (!def.visibleWhen || def.visibleWhen(values)) &&
    values[def.key] !== shownDefault(def);

  /** The count beside a section's name: switches on in Features, settings changed elsewhere. */
  const sectionCount = (sectionId: SectionId): { count: number; word: string } => {
    const inSection = FIELDS.filter((f) => f.section === sectionId);
    if (sectionId === 'features') return { count: inSection.filter(switchIsOn).length, word: 'on' };
    const extra = sectionId === 'advanced' && groupsNonDefault ? 1 : 0;
    return { count: inSection.filter(fieldChanged).length + extra, word: 'changed' };
  };

  // ---- payload builders ------------------------------------------------------
  /**
   * The values a save writes. `contractType` is the billing the contract means,
   * not a value the person picks, except "Billed as" on a Partner contract.
   */
  const payloadValues = (): FormValues =>
    contract.contract ? { ...values, contractType: billingFor(contract.contract, values.contractType) } : values;

  /**
   * @param materializeOnDefaults When true (the actual Save), force-write every
   *   ON switch that is absent from the record so on-by-default flags land in
   *   config.json, and every `alwaysWrite` key the record lacks. When false (the
   *   live "N unsaved changes" indicator), skip both, so a pristine open of an
   *   older record — one that never stored its defaults — reads "No unsaved
   *   changes" instead of nagging about keys the operator never touched. The
   *   writes still happen (and show in the confirm-diff preview) at Save time;
   *   they are just not counted as unsaved user edits.
   */
  const buildUpdatePayload = (materializeOnDefaults: boolean): Partial<ClientConfig> => {
    const cfg = (currentConfig ?? {}) as Record<string, unknown>;
    const updates: Record<string, unknown> = {};
    const vals = payloadValues();

    for (const def of FIELDS) {
      if (removedKeys.has(def.key)) {
        if (cfg[def.key] !== undefined) updates[def.key] = undefined;
        continue;
      }
      const newVal = serializeFieldValue(def, vals[def.key]);
      // Write alwaysWrite fields when the record lacks them, even at their default.
      if (def.alwaysWrite && cfg[def.key] === undefined) {
        if (materializeOnDefaults && newVal !== undefined) updates[def.key] = newVal;
        else if (newVal !== effectiveCurrentValue(def, cfg as Partial<ClientConfig>)) updates[def.key] = newVal;
        continue;
      }
      // Force-write ON switches that are absent from the record, so on-by-default
      // flags (default: true) materialize as an explicit `true` instead of hiding
      // behind the deploy-time default. Gated-off switches are skipped — a hidden
      // sub-flag must never be conjured in.
      if (
        materializeOnDefaults &&
        isOnSwitch(def, vals[def.key]) &&
        cfg[def.key] === undefined &&
        (!def.visibleWhen || def.visibleWhen(vals))
      ) {
        updates[def.key] = true;
        continue;
      }
      const eff = effectiveCurrentValue(def, cfg as Partial<ClientConfig>);
      if (eff === newVal) continue;
      // Both "absent" — nothing to remove
      if (newVal === undefined && cfg[def.key] === undefined) continue;
      updates[def.key] = newVal;
    }

    // App deployment (allApps / allProdApps / apps map)
    const allAppsNew = appMode === 'all';
    const allProdNew = appMode === 'prod';
    const effAllApps = Boolean(cfg.allApps);
    const effAllProd = Boolean(cfg.allProdApps);
    if (effAllApps !== allAppsNew) updates.allApps = allAppsNew;
    if (!allAppsNew && effAllProd !== allProdNew) updates.allProdApps = allProdNew;
    if (!allAppsNew) {
      const appsToInclude = allProdNew ? selectedApps.filter((a) => !PROD_APPS.includes(a)) : selectedApps;
      const currentApps = Object.keys((cfg.apps as Record<string, unknown> | undefined) || {});
      if (!sameMembers(currentApps, appsToInclude)) {
        updates.apps = appsToInclude.length > 0 ? Object.fromEntries(appsToInclude.map((a) => [a, {}])) : {};
      }
    }

    // Legacy field cleanup: `triggers` was renamed to `eventTriggers`. If a
    // record still carries the old key, drop it on save (REMOVE expression —
    // `undefined` value is the signal). Idempotent on subsequent saves.
    if (cfg.triggers !== undefined) updates.triggers = undefined;

    // Groups: include only when membership actually differs from the record's
    // effective groups (defaults when absent).
    const admin = stringToFeatures(groupAdmin);
    const standard = stringToFeatures(groupStandard);
    const currentGroups = cfg.groups as { admin?: string[]; standard?: string[] } | undefined;
    const currentAdmin = effectiveGroupList(currentGroups?.admin, DEFAULT_ADMIN_FEATURES);
    const currentStandard = effectiveGroupList(currentGroups?.standard, DEFAULT_STANDARD_FEATURES);
    if (!sameMembers(admin, currentAdmin) || !sameMembers(standard, currentStandard)) {
      updates.groups = {
        ...(standard.length ? { standard } : {}),
        ...(admin.length ? { admin } : {}),
      };
    }

    return updates as Partial<ClientConfig>;
  };

  const buildCreatePayload = (): Partial<ClientConfig> => {
    const minimal: Record<string, unknown> = {};
    const vals = payloadValues();

    for (const def of FIELDS) {
      // Fields hidden by their gate (e.g. Connect fields with telephony off) are
      // not written — their state is not part of the configured surface.
      if (def.visibleWhen && !def.visibleWhen(vals)) continue;
      const newVal = serializeFieldValue(def, vals[def.key]);
      if (def.key === 'region' || def.alwaysWrite || def.requiredOnCreate) {
        if (newVal !== undefined) minimal[def.key] = newVal;
        continue;
      }
      // Force-write ON switches even when they equal the schema default, so the
      // created config.json always lists enabled features (on-by-default flags
      // included). Gated-off fields were already skipped above.
      if (isOnSwitch(def, vals[def.key])) {
        minimal[def.key] = true;
        continue;
      }
      if (newVal === undefined) continue;
      const defaultSerialized = serializeFieldValue(def, presentedDefault(def));
      if (newVal !== defaultSerialized) minimal[def.key] = newVal;
    }

    // AgentCore region equal to the deployment region is redundant — drop it.
    if (minimal.agentCoreRegion !== undefined && minimal.agentCoreRegion === minimal.region) {
      delete minimal.agentCoreRegion;
    }

    // App deployment
    const allAppsNew = appMode === 'all';
    const allProdNew = appMode === 'prod';
    if (allAppsNew) minimal.allApps = true;
    else if (allProdNew) minimal.allProdApps = true;
    if (!allAppsNew) {
      const appsToInclude = allProdNew ? selectedApps.filter((a) => !PROD_APPS.includes(a)) : selectedApps;
      if (appsToInclude.length > 0) minimal.apps = Object.fromEntries(appsToInclude.map((a) => [a, {}]));
    }

    // Groups only when the membership differs from the recommended defaults
    const admin = stringToFeatures(groupAdmin);
    const standard = stringToFeatures(groupStandard);
    if (groupsNonDefault && (admin.length || standard.length)) {
      minimal.groups = {
        ...(standard.length ? { standard } : {}),
        ...(admin.length ? { admin } : {}),
      };
    }

    return minimal as Partial<ClientConfig>;
  };

  // Live indicator: count only genuine user edits — pass false so absent
  // on-by-default flags don't register as phantom pending changes on open.
  const pendingCount = useMemo(
    () =>
      Object.keys(mode === 'update' ? buildUpdatePayload(false) : buildCreatePayload()).length +
      (mode === 'update' ? extraPendingCount : 0),
    [
      values,
      removedKeys,
      appMode,
      selectedApps,
      groupAdmin,
      groupStandard,
      currentConfig,
      mode,
      contract,
      extraPendingCount,
    ]
  );

  // Update counts what the person changed. Create counts what the new config will hold.
  const pendingLabel =
    mode === 'update'
      ? pendingCount === 0
        ? 'No unsaved changes'
        : `${pendingCount} unsaved ${pendingCount === 1 ? 'change' : 'changes'}`
      : pendingCount === 0
        ? 'Every setting is at its default'
        : `${pendingCount} ${pendingCount === 1 ? 'setting' : 'settings'} to write`;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const contractIssue = contractProblem(contract);
    if (contractIssue) {
      setOpenSections((prev) => ({ ...prev, account: true }));
      onError(`${contractIssue} Every client needs a contract before the form saves.`);
      return;
    }
    if (mode === 'create') {
      const missing = missingRequiredOnCreate(values);
      if (missing.length) {
        onError(`Choose a value for ${missing.join(', ')} before you create the client.`);
        return;
      }
    }
    const errors = FIELDS.map((def) => {
      if (!def.validate) return null;
      // A key pending removal is never serialized — its stale stored value
      // must not block the save that removes it.
      if (removedKeys.has(def.key)) return null;
      // A hidden field is not saved, so its value cannot block the save.
      if (def.visibleWhen && !def.visibleWhen(values)) return null;
      const raw = String(values[def.key] ?? '');
      return raw ? def.validate(raw) : null;
    }).filter(Boolean) as string[];
    if (errors.length) {
      onError(errors.join('; '));
      return;
    }
    // Actual save: materialize on-by-default flags into the written record.
    onSubmit(mode === 'update' ? buildUpdatePayload(true) : buildCreatePayload());
  };

  // ---- filtering -------------------------------------------------------------
  const searchLower = search.trim().toLowerCase();
  const filtersActive = searchLower.length > 0;

  const fieldVisible = (def: FieldDef): boolean => {
    if (def.visibleWhen && !def.visibleWhen(values)) return false;
    if (searchLower && !searchHaystack(def).includes(searchLower)) return false;
    return true;
  };

  /** A field shows when it matches, or when a field under it does (so a search hit keeps its parent). */
  const blockVisible = (def: FieldDef): boolean => {
    if (def.bespoke) return !searchLower || CONTRACT_CONTROL_HAYSTACK.includes(searchLower);
    if (def.visibleWhen && !def.visibleWhen(values)) return false;
    return fieldVisible(def) || childrenOf(def.key).some(blockVisible);
  };

  const appsControlVisible = !searchLower || APPS_CONTROL_HAYSTACK.includes(searchLower);
  const groupsControlVisible = !searchLower || GROUPS_CONTROL_HAYSTACK.includes(searchLower);

  // Arcanum internal shows for an Internal contract, or for any client that
  // already has one of its switches on, so the switch can be seen and undone.
  const internalOffered = contract.contract === 'internal' || INTERNAL_FIELD_KEYS.some((key) => values[key] === true);

  const topLevelFields = (sectionId: SectionId, groupId?: string): FieldDef[] =>
    FIELDS.filter(
      (f) => f.section === sectionId && !f.childOf && (groupId === undefined || f.group === groupId) && blockVisible(f)
    );

  const sectionHasContent = (sectionId: SectionId): boolean => {
    if (sectionId === 'internal' && !internalOffered) return false;
    if (topLevelFields(sectionId).length > 0) return true;
    if (sectionId === 'account' && appsControlVisible) return true;
    if (sectionId === 'advanced' && groupsControlVisible) return true;
    return false;
  };

  const scrollToSection = (sectionId: SectionId) => {
    setOpenSections((prev) => ({ ...prev, [sectionId]: true }));
    // Defer until the Collapse has rendered its content
    setTimeout(() => {
      document.getElementById(`cfg-section-${sectionId}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  // ---- rendering ---------------------------------------------------------------
  /** An empty required field on the Create page leads with a "Choose one" it cannot keep. */
  const fieldOptions = (def: FieldDef) => {
    const options = def.dynamicOptions ? def.dynamicOptions(values) : def.options;
    if (mode !== 'create' || !def.requiredOnCreate || !options || values[def.key] !== '') return options;
    return [{ label: 'Choose one (required)', value: '', disabled: true }, ...options];
  };

  const renderField = (def: FieldDef) => {
    const record = (currentConfig ?? {}) as Record<string, unknown>;
    const locked = mode === 'update' && !!def.lockedOnUpdate;
    const fieldDisabled = locked || (def.disabledWhen ? def.disabledWhen(values) : false);
    const removable =
      mode === 'update' &&
      !locked &&
      record[def.key] !== undefined &&
      def.key !== 'region' &&
      !def.alwaysWrite &&
      !removedKeys.has(def.key);
    return (
      <ConfigField
        key={def.key}
        fieldKey={def.key}
        label={def.label}
        value={values[def.key]}
        // A locked value is not a choice here, so it never reads as "not default".
        defaultValue={locked ? values[def.key] : shownDefault(def)}
        defaultLabel={def.defaultLabel}
        newClientDefault={
          mode === 'update' && !locked && def.createDefault !== undefined && def.createDefault !== shownDefault(def)
            ? def.createDefault
            : undefined
        }
        showDefault={!locked}
        onChange={(v: string | boolean) => setValue(def.key, v)}
        type={def.type}
        options={fieldOptions(def)}
        placeholder={def.inheritKey ? inheritPlaceholder(def.inheritKey, def.inheritSuffix) : def.placeholder}
        helpText={def.help}
        warnWhenSet={def.warnWhenSet}
        disabled={fieldDisabled}
        disabledNote={locked ? def.lockedOnUpdate : fieldDisabled ? def.disabledNote : undefined}
        removable={removable}
        removed={removedKeys.has(def.key)}
        onRemove={() => {
          markEdited();
          setRemovedKeys((prev) => new Set(prev).add(def.key));
        }}
        onUndoRemove={() => {
          markEdited();
          setRemovedKeys((prev) => {
            const next = new Set(prev);
            next.delete(def.key);
            return next;
          });
        }}
        removeFallbackNote={removeFallbackNote(def)}
      />
    );
  };

  /** The contract dropdown in one column, and what it asks for next (partner, trial dates) on a row of its own. */
  const renderContract = () => {
    const shared = {
      value: contract,
      onChange: changeContract,
      billing: String(values.contractType),
      onBillingChange: (billing: Billing) => setValue('contractType', billing),
      partnerNames,
      suggestion: contractSuggestion,
    };
    const hasDetails = contract.contract === 'partner' || contract.contract === 'trial';
    return (
      <Fragment key="contract">
        <div className="config-form__cell">
          <ContractFields {...shared} part="select" />
        </div>
        {hasDetails && (
          <div className="config-form__cell config-form__cell--wide">
            <ContractFields {...shared} part="details" />
          </div>
        )}
      </Fragment>
    );
  };

  /** Children under a field, in a grid, under their sub-headings when they have them. */
  const renderChildren = (children: FieldDef[]) => {
    const ungrouped = children.filter((c) => !c.childGroup);
    const groupIds = [...new Set(children.map((c) => c.childGroup).filter(Boolean))] as string[];
    return (
      <div className="config-form__children">
        {ungrouped.length > 0 && <div className="config-form__grid">{ungrouped.map(renderBlock)}</div>}
        {groupIds.map((groupId) => (
          <Fragment key={groupId}>
            <SubHeader title={CHILD_GROUPS[groupId].title} description={CHILD_GROUPS[groupId].description} />
            <div className="config-form__grid">{children.filter((c) => c.childGroup === groupId).map(renderBlock)}</div>
          </Fragment>
        ))}
      </div>
    );
  };

  /** A field, or one of its settings at any depth, holds a value that is not its default. */
  const descendantChanged = (def: FieldDef): boolean =>
    childrenOf(def.key).some(
      (c) =>
        (!c.visibleWhen || c.visibleWhen(values)) &&
        ((!c.bespoke && values[c.key] !== shownDefault(c)) || descendantChanged(c))
    );

  /**
   * A field and, at any depth, the settings under it. The settings fold
   * behind a toggle under the field. They start open when one of them is not
   * at its default, and always show during a search. A field whose settings
   * are open takes the whole row, so they sit directly under it.
   */
  const renderBlock = (def: FieldDef): ReactElement | null => {
    if (def.bespoke) return def.key === 'contractType' ? renderContract() : null;
    if (!blockVisible(def)) return null;
    const children = childrenOf(def.key).filter(blockVisible);
    const changedBelow = children.length > 0 && descendantChanged(def);
    const open = children.length > 0 && (filtersActive || (openSettings[def.key] ?? changedBelow));
    const wide = def.wide || open;
    return (
      <div
        key={def.key}
        className={`config-form__cell${wide ? ' config-form__cell--wide' : ''}${open ? ' config-form__cell--parent' : ''}`}
      >
        {fieldVisible(def) && renderField(def)}
        {children.length > 0 && !filtersActive && (
          <button
            type="button"
            className="config-form__settings-toggle"
            aria-expanded={open}
            onClick={() => setOpenSettings((prev) => ({ ...prev, [def.key]: !open }))}
          >
            {open ? <ChevronDown aria-hidden="true" /> : <ChevronRight aria-hidden="true" />}
            {def.label} settings
            <span className="config-form__settings-count">
              {children.length}
              {changedBelow ? ', some changed' : ''}
            </span>
          </button>
        )}
        {open && renderChildren(children)}
      </div>
    );
  };

  const renderAppsControl = () => (
    <div key="apps" className="config-form__cell config-form__cell--wide">
      <Form.Group className="config-field">
        <div className="config-field__head">
          <div className="config-field__title">
            <Form.Label className="config-field__label">Apps to deploy</Form.Label>
            <span className="config-field__default">default no apps</span>
          </div>
        </div>
        <Form.Check
          type="radio"
          id="app-mode-none"
          name="app-mode"
          label="No apps."
          checked={appMode === 'none'}
          onChange={() => selectAppMode('none')}
        />
        <Form.Check
          type="radio"
          id="app-mode-custom"
          name="app-mode"
          label="Choose apps: tick the ones the client needs."
          checked={appMode === 'custom'}
          onChange={() => selectAppMode('custom')}
        />
        <Form.Check
          type="radio"
          id="app-mode-prod"
          name="app-mode"
          label="Production apps: every production app. You can add dev apps below."
          checked={appMode === 'prod'}
          onChange={() => selectAppMode('prod')}
        />
        <Form.Check
          type="radio"
          id="app-mode-all"
          name="app-mode"
          label="All apps: every app in the library. For dev and demo stacks."
          checked={appMode === 'all'}
          onChange={() => selectAppMode('all')}
        />
        {appMode !== 'none' && (
          <div className="config-form__apps">
            {ALL_APPS.map((a) => {
              const isProdApp = PROD_APPS.includes(a);
              // Synergy Explorer is driven by the `synergy` toggle, not by hand:
              // ticking it while synergy is off would show an app that infra
              // silently drops at deploy.
              const isSynergyApp = a === SYNERGY_APP_ID;
              const synergyOn = values.synergy === true;
              const isChecked = isSynergyApp
                ? synergyOn
                : appMode === 'all' || (appMode === 'prod' && isProdApp) || selectedApps.includes(a);
              const isDisabled = isSynergyApp || appMode === 'all' || (appMode === 'prod' && isProdApp);
              return (
                <Form.Check
                  key={a}
                  type="checkbox"
                  id={`app-${a}`}
                  label={isSynergyApp ? `${a} (follows Synergy 12d)` : a}
                  checked={isChecked}
                  disabled={isDisabled}
                  onChange={(e) => {
                    const checked = e.currentTarget.checked;
                    markEdited();
                    setSelectedApps((prev) =>
                      checked ? Array.from(new Set([...prev, a])) : prev.filter((x) => x !== a)
                    );
                  }}
                />
              );
            })}
          </div>
        )}
        <Form.Text className="config-field__help">
          {appMode === 'none'
            ? values.synergy === true
              ? 'No apps, apart from Synergy Explorer, which follows the Synergy 12d switch.'
              : 'The client gets no apps. Dashboards and the App Builder do not need any.'
            : appMode === 'all'
              ? 'Every app is included.'
              : appMode === 'prod'
                ? 'Every production app is included. Tick a dev app to add it.'
                : selectedApps.length === 0
                  ? 'No apps. Tick the apps to deploy.'
                  : `${selectedApps.length} ${selectedApps.length === 1 ? 'app' : 'apps'} ticked.`}
        </Form.Text>
      </Form.Group>
    </div>
  );

  const renderGroupsControl = () => (
    <div className="config-form__cell config-form__cell--wide">
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Standard users</Form.Label>
            <FeatureChecklist role="standard" value={groupStandard} onChange={editGroupStandard} />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Admins</Form.Label>
            <FeatureChecklist role="admin" value={groupAdmin} onChange={editGroupAdmin} />
          </Form.Group>
        </Col>
      </Row>
    </div>
  );

  const renderSectionBody = (sectionId: SectionId) => {
    const section = CONFIG_SECTIONS.find((s) => s.id === sectionId)!;
    if (sectionId === 'account') {
      return (
        <div className="config-form__grid">
          {topLevelFields('account').map(renderBlock)}
          {appsControlVisible && renderAppsControl()}
        </div>
      );
    }
    if (!section.groups) {
      return <div className="config-form__grid">{topLevelFields(sectionId).map(renderBlock)}</div>;
    }
    return section.groups.map((group) => {
      const isRoles = sectionId === 'advanced' && group.id === 'roles';
      const fields = topLevelFields(sectionId, group.id);
      if (!isRoles && fields.length === 0) return null;
      if (isRoles && !groupsControlVisible) return null;
      return (
        <Fragment key={group.id}>
          <SubHeader title={group.title} description={group.description} />
          {isRoles ? renderGroupsControl() : <div className="config-form__grid">{fields.map(renderBlock)}</div>}
        </Fragment>
      );
    });
  };

  const renderSection = (sectionId: SectionId) => {
    const section = CONFIG_SECTIONS.find((s) => s.id === sectionId)!;
    if (!sectionHasContent(sectionId)) return null;
    const open = filtersActive || openSections[sectionId];
    const { count, word } = sectionCount(sectionId);
    return (
      <Card
        key={sectionId}
        className={`config-form__section config-form__section--${sectionId}`}
        id={`cfg-section-${sectionId}`}
      >
        <button
          type="button"
          className="config-form__section-head"
          onClick={() => setOpenSections((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }))}
          aria-expanded={Boolean(open)}
          aria-controls={`cfg-section-${sectionId}-body`}
        >
          {open ? <ChevronDown aria-hidden="true" /> : <ChevronRight aria-hidden="true" />}
          <span className="config-form__section-title">{section.title}</span>
          {section.description && <span className="config-form__section-desc">{section.description}</span>}
          {count > 0 && (
            <Badge bg="secondary" className="ms-auto">
              {count} {word}
            </Badge>
          )}
        </button>
        <Collapse in={open}>
          <div id={`cfg-section-${sectionId}-body`}>
            <Card.Body className="config-form__section-body">
              {sectionId === 'internal' && contract.contract !== 'internal' && (
                <div className="config-field__note config-field__note--warning mb-3">
                  This client is not on an Internal contract, and a switch here is on. Turn it off unless the client is
                  one of Arcanum's own stacks.
                </div>
              )}
              {renderSectionBody(sectionId)}
            </Card.Body>
          </div>
        </Collapse>
      </Card>
    );
  };

  // The submit button can sit outside the <form> (in the page's left column), so it names the form by id.
  const formId = `client-config-form-${mode}`;

  /** Search, the sections and save. In the page's left column when the page gives a slot, else beside the form. */
  const rail = (
    <div className={`config-form__rail${railHost !== undefined ? ' config-form__rail--hosted' : ''}`}>
      <Card>
        <Card.Body className="config-form__rail-body">
          <Form.Control
            size="sm"
            type="search"
            placeholder="Search settings"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              // Never let Enter in the search box implicitly submit the form
              if (e.key === 'Enter') e.preventDefault();
            }}
            aria-label="Search settings"
          />
          <nav className="config-form__nav" aria-label="Config sections">
            {CONFIG_SECTIONS.map((s) => {
              if (!sectionHasContent(s.id)) return null;
              const { count, word } = sectionCount(s.id);
              return (
                <button
                  key={s.id}
                  type="button"
                  className="config-form__nav-link"
                  onClick={() => scrollToSection(s.id)}
                >
                  <span className="text-truncate">{s.title}</span>
                  {count > 0 && (
                    <span className="config-form__nav-count" title={`${count} ${word}`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
          <div className="d-grid">
            <Button type="submit" form={formId} variant="primary" disabled={working || submitDisabled}>
              {working ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Saving
                </>
              ) : (
                submitLabel
              )}
            </Button>
          </div>
          {justSaved && pendingCount === 0 ? (
            <div className="config-form__status config-form__status--saved">
              <Check2 aria-hidden="true" /> Saved
            </div>
          ) : (
            <div className="config-form__status">{pendingLabel}</div>
          )}
          {submitHint && <div className="config-form__status">{submitHint}</div>}
        </Card.Body>
      </Card>
    </div>
  );

  const sections = (
    <>
      {filtersActive && !CONFIG_SECTIONS.some((s) => sectionHasContent(s.id)) && (
        <Card>
          <Card.Body className="config-form__empty">
            No setting matches "{search.trim()}".{' '}
            <Button variant="link" className="p-0 align-baseline" onClick={() => setSearch('')}>
              Clear the search
            </Button>
          </Card.Body>
        </Card>
      )}
      {CONFIG_SECTIONS.map((s) => renderSection(s.id))}
    </>
  );

  // The page holds the layout: the rail goes into its slot, the sections fill the form.
  if (railHost !== undefined) {
    return (
      <>
        {railHost && createPortal(rail, railHost)}
        <Form id={formId} onSubmit={handleSubmit} className="config-form">
          {sections}
        </Form>
      </>
    );
  }

  return (
    <Form id={formId} onSubmit={handleSubmit} className="config-form">
      <Row>
        {/* Sticky rail: search, section nav, save. It stays in view down the whole form. */}
        <Col md={4} lg={3} xl={2} className="mb-3">
          {rail}
        </Col>
        <Col md={8} lg={9} xl={10}>
          {sections}
        </Col>
      </Row>
    </Form>
  );
}

export default ClientConfigForm;
